/**
 * Content Block Factory
 *
 * Factory component that maps content block types to their corresponding
 * React components. This provides a clean abstraction for rendering
 * different content types dynamically.
 *
 * ⚠ REQUIRED companion edit: `ContentBlockType` in '@/types/template.types'
 * must add the literals 'books' | 'book_detail' to its union (that file
 * wasn't available to edit directly here) — without it, BLOCK_COMPONENTS /
 * typeLabels below won't type-check, since both are keyed by every member
 * of ContentBlockType.
 */

'use client';

import React, { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { ContentItem, ContentBlockType } from '@/types/template.types';
import { ErrorBoundary } from '@/app/shared/components/ui/ErrorBoundary';
import { ContentBlockSkeleton } from '@/app/shared/components/ui/ContentBlockSkeleton';

// Lazy load components for better performance
const NewsBlock = dynamic(() => import('./NewsBlock'), {
  loading: () => <ContentBlockSkeleton type="news" />,
});

const VideoBlock = dynamic(() => import('./VideoBlock'), {
  loading: () => <ContentBlockSkeleton type="video" />,
});

const CategoryBlock = dynamic(() => import('./CategoryBlock'), {
  loading: () => <ContentBlockSkeleton type="categories" />,
});

const AdBlock = dynamic(() => import('./AdBlock'), {
  loading: () => <ContentBlockSkeleton type="ads" />,
});

const ProfileBlock = dynamic(() => import('./ProfileBlock'), {
  loading: () => <ContentBlockSkeleton type="profiles" />,
});

const GalleryBlock = dynamic(() => import('./GalleryBlock'), {
  loading: () => <ContentBlockSkeleton type="gallery" />,
});

// ── Book Listing / Book Detail widgets ──────────────────────────────────────
// BookBlock.tsx / BookDetailView.tsx + the books/ engine folder live in this
// same content-blocks directory, as siblings of GalleryBlock.tsx (same
// relationship gallery has). ContentBlockSkeleton doesn't have dedicated
// "books"/"book_detail" cases (that file wasn't available to extend), so
// both reuse the visually-closest existing skeleton ("gallery" — grid of
// image cards) rather than falling back to a generic "text" placeholder.
const BookBlock = dynamic(() => import('./BookBlock'), {
  loading: () => <ContentBlockSkeleton type="gallery" />,
});

const BookDetailBlock = dynamic(() => import('./BookDetailView'), {
  loading: () => <ContentBlockSkeleton type="gallery" />,
});

const TextBlock = dynamic(() => import('./TextBlock'), {
  loading: () => <ContentBlockSkeleton type="text" />,
});

const ImagesBlock = dynamic(() => import('./ImagesBlock'), {
  loading: () => <ContentBlockSkeleton type="images" />,
});

const ItemDetailBlock = dynamic(() => import('./ItemDetailBlock'), {
  loading: () => <ContentBlockSkeleton type="item_detail" />,
});

const IncludeFileBlock = dynamic(() => import('./IncludeFileBlock'), {
  loading: () => <ContentBlockSkeleton type="text" />,
});

const FormSectionBlock = dynamic(() => import('./FormSectionBlock'), {
  loading: () => <ContentBlockSkeleton type="text" />,
});

const RawJsonBlock = dynamic(() => import('./RawJsonBlock'), {
  loading: () => <ContentBlockSkeleton type="text" />,
});

// ============================================================================
// Component Mapping
// ============================================================================

const BLOCK_COMPONENTS: Record<ContentBlockType, React.ComponentType<any>> = {
  news: NewsBlock,
  video: VideoBlock,
  categories: CategoryBlock,
  ads: AdBlock,
  profiles: ProfileBlock,
  gallery: GalleryBlock,
  // ── Book Listing / Book Detail widgets ────────────────────────────────────
  // 'books' = data-type="books" (Book Listing) and 'book_detail' =
  // data-type="book_detail" (Book Detail) in templatedesigner.php. Both are
  // registered as their own top-level block types here — same pattern
  // 'gallery' uses — rather than folded into the generic 'item_detail'
  // dispatch, since the template designer treats them as distinct
  // draggable widgets.
  books: BookBlock,
  book_detail: BookDetailBlock,
  text: TextBlock,
  images: ImagesBlock,
  item_detail: ItemDetailBlock,
  include_file: IncludeFileBlock,
  // ── Form Section widget ──────────────────────────────────────────────────
  // Renders dynamic form sections built by form-section-widget.js in the CMS.
  // Config shape: { form_section_data: FormSectionData } — see FormSectionBlock.tsx
  form_section: FormSectionBlock,
  // ── Universal JSON widget ────────────────────────────────────────────────
  // A single hand-written {type, config} item, built by json-widget.js in
  // the CMS. Parsed and re-dispatched through THIS factory at render time —
  // see RawJsonBlock.tsx. Config shape: { item_json: string }.
  raw_json: RawJsonBlock,
};

// ============================================================================
// Content Block Factory Props
// ============================================================================

interface ContentBlockFactoryProps {
  item: ContentItem;
  categoryId?: string;
  /** Raw API record — only used by item_detail blocks */
  record?: Record<string, unknown> | null;
  className?: string;
}

// ============================================================================
// Content Block Factory Component
// ============================================================================

export const ContentBlockFactory: React.FC<ContentBlockFactoryProps> = ({
  item,
  categoryId,
  record,
  className = '',
}) => {
  // Validate item structure
  if (!item || !item.type || !item.config) {
    console.error('Invalid content item:', item);
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800 text-sm">Invalid content block configuration</p>
      </div>
    );
  }

  // Get the appropriate component for this content type
  const BlockComponent = BLOCK_COMPONENTS[item.type];

  // Handle unknown content types
  if (!BlockComponent) {
    console.warn(`Unknown content block type: ${item.type}`);
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-gray-600 text-sm">
          Unknown content type: <code className="bg-gray-200 px-2 py-1 rounded">{item.type}</code>
        </p>
      </div>
    );
  }

  // Render the component with error boundary and suspense
  return (
    <ErrorBoundary
      fallback={
        <ContentBlockError 
          type={item.type} 
          onRetry={() => window.location.reload()} 
        />
      }
    >
      <Suspense fallback={<ContentBlockSkeleton type={item.type} />}>
        <BlockComponent
          config={item.config}
          categoryId={categoryId}
          record={record}
          className={className}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

// ============================================================================
// Content Block Error Component
// ============================================================================

interface ContentBlockErrorProps {
  type: ContentBlockType;
  onRetry?: () => void;
  error?: Error;
}

const ContentBlockError: React.FC<ContentBlockErrorProps> = ({ 
  type, 
  onRetry,
  error 
}) => {
  const typeLabels: Record<ContentBlockType, string> = {
    news: 'News',
    video: 'Video',
    categories: 'Categories',
    ads: 'Advertisements',
    profiles: 'Profiles',
    gallery: 'Gallery',
    books: 'Book Listing',
    book_detail: 'Book Detail',
    text: 'Text Content',
    images: 'Images',
    item_detail: 'Item Details',
    include_file: 'Include File',
    form_section: 'Form Section',
    raw_json: 'JSON Widget',
  };

  return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-start gap-3">
        <svg 
          className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
        <div className="flex-1">
          <h3 className="text-red-800 font-semibold mb-1">
            Failed to load {typeLabels[type] || type}
          </h3>
          {error && (
            <p className="text-red-600 text-sm mb-3">
              {error.message}
            </p>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-sm text-red-700 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Multiple Blocks Renderer
// ============================================================================

interface ContentBlocksRendererProps {
  items: ContentItem[];
  categoryId?: string;
  record?: Record<string, unknown> | null;
  className?: string;
}

export const ContentBlocksRenderer: React.FC<ContentBlocksRendererProps> = ({
  items,
  categoryId,
  record,
  className = '',
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`content-blocks-container space-y-6 ${className}`}>
      {items.map((item, index) => (
        <ContentBlockFactory
          key={`${item.type}-${index}`}
          item={item}
          categoryId={categoryId}
          record={record}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Type-specific Renderers (for convenience)
// ============================================================================

export const renderNewsBlock = (config: any, categoryId?: string) => (
  <ContentBlockFactory 
    item={{ type: 'news', config }} 
    categoryId={categoryId}
  />
);

export const renderVideoBlock = (config: any, categoryId?: string) => (
  <ContentBlockFactory 
    item={{ type: 'video', config }} 
    categoryId={categoryId}
  />
);

export const renderCategoryBlock = (config: any, categoryId?: string) => (
  <ContentBlockFactory 
    item={{ type: 'categories', config }} 
    categoryId={categoryId}
  />
);

export default ContentBlockFactory;